class ComboBox extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.options = [];
    this.filteredOptions = [];
    this.highlightedIndex = -1;
    this.blurTimeout = null;
    this.placeholder = this.getAttribute('data-placeholder');
    this.value = this.getAttribute('data-value');
    this.inputId = `combo-input-${Math.random().toString(36).slice(2)}`;
    this.listboxId = `combo-listbox-${Math.random().toString(36).slice(2)}`;

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: inline-block;
          position: relative;
          font-family: sans-serif;
        }
        ::slotted(input) {
          width: 100%;
          padding: 12px 44px 12px 12px;
          box-sizing: border-box !important;
          background: white;
          font-size: 1.5rem;
        }
        .combo-box__toggle {
          position: absolute;
          top: 0;
          right: 0;
          width: 44px;
          height: 100%;
          margin: 0;
          padding: 0;
          border: 0;
          background: transparent;
          cursor: pointer;
          display: grid;
          place-items: center;
          z-index: 2;
        }
        .combo-box__toggle svg {
          width: 24px;
          height: 24px;
          pointer-events: none;
          transition: transform 0.15s ease;
        }
        :host([open]) .combo-box__toggle svg {
          transform: rotate(180deg);
        }
        ul {
          list-style: none;
          margin: 0;
          padding: 0;
          border: 1px solid #ccc;
          max-height: 150px;
          overflow-y: scroll;
          position: absolute;
          width: 100%;
          background: white;
          display: none;
          z-index: 100;
          border: 1px solid black;
          border-top: 0 none;
          box-sizing: border-box;
        }
        ul::-webkit-scrollbar-track {
          -webkit-box-shadow: none;
          background-color: #F5F5F5;
        }

        ul::-webkit-scrollbar {
          width: 4px;
          background-color: #F5F5F5;
        }

        ul::-webkit-scrollbar-thumb {
          background-color: #000000;
          border: 2px solid #555555;
        }
        li {
          padding: 8px;
          cursor: pointer;
          font-size: 1.5rem;
        }
        li.highlighted,
        li:focus {
          background-color: #007bff;
          color: white;
          outline: none;
        }
        li:hover {
          background: #eee;
        }
      </style>
      <slot name="input"></slot>
      <button
        type="button"
        class="combo-box__toggle"
        tabindex="-1"
        aria-label="Toggle list"
      >
        <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24" fill="currentColor" aria-hidden="true">
          <path d="M480-361q-8 0-15-2.5t-13-8.5L268-556q-11-11-11-28t11-28q11-11 28-11t28 11l156 156 156-156q11-11 28-11t28 11q11 11 11 28t-11 28L508-372q-6 6-13 8.5t-15 2.5Z"/>
        </svg>
      </button>
      <ul role="listbox" id="${this.listboxId}"></ul>
    `;
  }

  connectedCallback() {
    this.input = this.querySelector('[slot="input"]');
    this.dropdown = this.shadowRoot.querySelector('ul');
    this.toggle = this.shadowRoot.querySelector('.combo-box__toggle');

    if (!this.input) {
      console.warn('combo-box requires an <input slot="input"> child.');
      return;
    }

    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-haspopup', 'listbox');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-controls', this.listboxId);
    if (!this.input.getAttribute('aria-label') && this.placeholder) {
      this.input.setAttribute('aria-label', this.placeholder);
    }

    this.input.addEventListener('input', () => this.filterOptions());
    this.input.addEventListener('focus', () => {
      clearTimeout(this.blurTimeout);
      this.showOptions();
    });
    this.input.addEventListener('click', () => this.showOptions());
    this.input.addEventListener('blur', () => {
      this.blurTimeout = setTimeout(() => this.hideOptions(), 200);
    });
    this.input.addEventListener('keydown', (e) => this.handleKeyDown(e));
    this.addEventListener('focusout', () => {
      setTimeout(() => {
        if (this.contains(document.activeElement) || this.shadowRoot.activeElement) return;
        this.hideOptions();
      }, 0);
    });

    this.toggle.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.toggleList();
    });

    this.dropdown.addEventListener('click', (e) => {
      if (e.target.tagName === 'LI') {
        const index = parseInt(e.target.getAttribute('data-index'), 10);
        const option = this.filteredOptions[index];
        this.selectOption(option);
      }
    });

    this.dropdown.addEventListener('focusin', (e) => {
      const option = e.target.closest('li');
      if (!option) return;
      this.updateHighlight(parseInt(option.getAttribute('data-index'), 10));
    });

    this.dropdown.addEventListener('keydown', (e) => this.handleOptionKeyDown(e));

    this.loadOptions();
  }

  loadOptions() {
    const data = JSON.parse(this.getAttribute('options'));
    this.options = data.map((opt) => (typeof opt === 'string' ? { label: opt, value: opt } : opt));
    this.filteredOptions = [...this.options];
    this.renderOptions();
  }

  filterOptions() {
    const value = this.input.value.toLowerCase();
    this.filteredOptions = this.options.filter((opt) => opt.label.toLowerCase().includes(value));
    this.renderOptions();
    this.showOptions();
  }

  renderOptions(keepHighlight = false) {
    this.dropdown.innerHTML = '';
    if (!keepHighlight) {
      this.highlightedIndex = this.filteredOptions.findIndex((opt) => opt.label === this.input.value);
    }
    if (this.highlightedIndex >= this.filteredOptions.length) {
      this.highlightedIndex = this.filteredOptions.length - 1;
    }
    this.filteredOptions.forEach((option, index) => {
      const li = document.createElement('li');
      li.textContent = option.label;
      li.setAttribute('data-index', index);
      li.setAttribute('role', 'option');
      li.setAttribute('part', 'option');
      li.id = `${this.listboxId}-option-${index}`;
      li.tabIndex = 0;
      li.setAttribute('aria-selected', index === this.highlightedIndex ? 'true' : 'false');
      if (index === this.highlightedIndex) {
        li.classList.add('highlighted');
        this.input.setAttribute('aria-activedescendant', li.id);
      }
      this.dropdown.appendChild(li);
    });

    if (this.highlightedIndex === -1) {
      this.input.removeAttribute('aria-activedescendant');
    } else {
      this.scrollHighlightedIntoView();
    }
  }

  updateHighlight(index) {
    if (Number.isNaN(index) || index < 0 || index >= this.filteredOptions.length) return;
    this.highlightedIndex = index;
    this.dropdown.querySelectorAll('li').forEach((li, i) => {
      const isActive = i === index;
      li.classList.toggle('highlighted', isActive);
      li.setAttribute('aria-selected', isActive ? 'true' : 'false');
      if (isActive) {
        this.input.setAttribute('aria-activedescendant', li.id);
      }
    });
    this.scrollHighlightedIntoView();
  }

  scrollHighlightedIntoView() {
    const item = this.dropdown.querySelector('li.highlighted');
    if (!item) return;

    const list = this.dropdown;
    const itemTop = item.offsetTop;
    const itemBottom = itemTop + item.offsetHeight;
    const viewTop = list.scrollTop;
    const viewBottom = viewTop + list.clientHeight;

    if (itemTop < viewTop) {
      list.scrollTop = itemTop;
    } else if (itemBottom > viewBottom) {
      list.scrollTop = itemBottom - list.clientHeight;
    }
  }

  isOpen() {
    return this.hasAttribute('open');
  }

  toggleList() {
    if (this.isOpen()) {
      this.hideOptions();
      return;
    }

    this.input.focus();
    this.showOptions();
  }

  showOptions() {
    if (this.filteredOptions.length > 0) {
      this.dropdown.style.display = 'block';
      this.input.setAttribute('aria-expanded', 'true');
      if (this.highlightedIndex < 0) {
        this.highlightedIndex = 0;
        this.renderOptions(true);
      }
      this.setAttribute('open', '');
    }
  }

  hideOptions() {
    this.dropdown.style.display = 'none';
    this.input.setAttribute('aria-expanded', 'false');
    this.removeAttribute('open');
  }

  selectOption(option) {
    this.input.value = option.label;
    this.hideOptions();
    this.dispatchEvent(new CustomEvent('select', { detail: { option }, bubbles: true }));
  }

  handleKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.showOptions();
      if (!this.filteredOptions.length) return;
      this.highlightedIndex =
        this.highlightedIndex < this.filteredOptions.length - 1 ? this.highlightedIndex + 1 : 0;
      this.updateHighlight(this.highlightedIndex);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.showOptions();
      if (!this.filteredOptions.length) return;
      this.highlightedIndex =
        this.highlightedIndex > 0 ? this.highlightedIndex - 1 : this.filteredOptions.length - 1;
      this.updateHighlight(this.highlightedIndex);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (this.highlightedIndex >= 0 && this.highlightedIndex < this.filteredOptions.length) {
        this.selectOption(this.filteredOptions[this.highlightedIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      this.hideOptions();
    } else if (e.key === 'Tab' && !e.shiftKey && this.input.getAttribute('aria-expanded') === 'true') {
      e.preventDefault();
      this.focusOption(this.highlightedIndex >= 0 ? this.highlightedIndex : 0);
    }
  }

  handleOptionKeyDown(e) {
    const option = e.target.closest('li');
    if (!option) return;

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const index = parseInt(option.getAttribute('data-index'), 10);
      this.selectOption(this.filteredOptions[index]);
      return;
    }

    if (e.key !== 'Tab') return;

    const index = parseInt(option.getAttribute('data-index'), 10);
    if (!e.shiftKey && index < this.filteredOptions.length - 1) {
      e.preventDefault();
      this.focusOption(index + 1);
    } else if (e.shiftKey && index > 0) {
      e.preventDefault();
      this.focusOption(index - 1);
    } else if (e.shiftKey && index === 0) {
      e.preventDefault();
      this.input.focus({ preventScroll: true });
    } else if (!e.shiftKey) {
      this.hideOptions();
    }
  }

  focusOption(index) {
    const item = this.dropdown.querySelector(`li[data-index="${index}"]`);
    if (!item) return;
    item.focus({ preventScroll: true });
    this.updateHighlight(index);
  }
}

customElements.define('combo-box', ComboBox);
